/** Rebuild the data model from the stored data: what it does, start, progress per Vault, cancel, the result. */
import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Api, type RebuildStatus, type RebuildVault } from '../lib/api.ts';
import { useAsync } from '../lib/hooks.ts';
import { count, dateTime, duration } from '../lib/format.ts';
import { cx, IconButton } from '../design/primitives.tsx';
import { CloseIcon } from './icons.tsx';
import { Button, Spinner, useToast } from './ui.tsx';

const RUNNING = 'running';

/** Share of the scan done, 0..1 (partitions done, refined by documents when the total is known). */
export function progressOf(s: Pick<RebuildStatus, 'partitionsTotal' | 'partitionsDone' | 'docsScanned' | 'docsTotalEstimate'>): number {
  if (s.docsTotalEstimate && s.docsTotalEstimate > 0) return Math.min(1, (s.docsScanned ?? 0) / s.docsTotalEstimate);
  return s.partitionsTotal ? Math.min(1, (s.partitionsDone ?? 0) / s.partitionsTotal) : 0;
}

export const ALL = '*';

/** `scope` is the message class to rebuild, or ALL for the whole data model of the Vaults. */
export function RebuildModel({ tid, scope, label, onScope, onClose, onDone }: {
  tid: string; scope: string; label: string; onScope: (scope: string) => void; onClose: () => void; onDone: () => void;
}) {
  const toast = useToast();
  const status = useAsync(() => Api.rebuildStatus(tid), [tid], 2000);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const vaults = status.data?.vaults ?? [];
  const running = vaults.some((v) => v.status?.state === RUNNING);
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) onDone();
    wasRunning.current = running;
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (action: 'start' | 'cancel') => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await Api.rebuildAct(tid, action, scope === ALL ? undefined : scope);
      const failed = r.vaults.find((v) => v.error);
      if (failed) setError(`${failed.vaultName}: ${failed.error}`);
      else toast(action === 'start' ? 'Rebuild started' : 'Rebuild cancelled');
      status.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]" />
        <Dialog.Content aria-describedby={undefined}
          className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(720px,94vw)] max-h-[88vh] overflow-y-auto flex flex-col gap-4 bg-n2 text-n10 rounded-card shadow-float p-5 outline-none mv-pop">
          <div className="flex items-start gap-3">
            <div className="flex-1 flex flex-col gap-1">
              <Dialog.Title className="m-0 text-body font-semibold">{scope === ALL ? 'Rebuild all data models from the data' : `Rebuild ${label} from the data`}</Dialog.Title>
              <p className="m-0 text-small text-n8">
                Each Vault reads the documents it has stored {scope === ALL ? 'and builds all its data models again' : <>of <span className="font-mono">{scope}</span> and builds this data model again; the other data models are not touched</>}: exact counts and types, and the fields of data that is gone disappear.
                It runs in the background, new documents keep arriving meanwhile and nothing is lost. The old model stays in use until the new one is ready, and a rebuild that is cancelled or fails changes nothing.
                Types you have declared are kept.
              </p>
            </div>
            <Dialog.Close asChild><IconButton label="Close"><CloseIcon size={16} /></IconButton></Dialog.Close>
          </div>

          {status.loading && !status.data && <div className="py-4 grid place-items-center"><Spinner /></div>}
          {status.error && <p className="m-0 text-small text-fail">{status.error}</p>}
          {error && <p className="m-0 text-small text-fail">{error}</p>}
          {status.data && vaults.length === 0 && <p className="m-0 text-small text-n8">No Vault of this environment is online.</p>}

          <ul className="m-0 p-0 list-none flex flex-col gap-3">
            {vaults.map((v) => <VaultRow key={v.vaultId} v={v} />)}
          </ul>
          {status.data && status.data.offline.length > 0 && <p className="m-0 text-small text-n8">Offline, not rebuilt: {status.data.offline.join(', ')}.</p>}

          <div className="flex justify-end gap-2 flex-wrap">
            {!running && (
              <Button variant="ghost" onClick={() => onScope(scope === ALL ? '' : ALL)} className="mr-auto">
                {scope === ALL ? 'Only this data model instead' : 'Rebuild all data models instead'}
              </Button>
            )}
            <Button variant="ghost" onClick={onClose}>Close</Button>
            {running
              ? <Button variant="danger" disabled={busy} onClick={() => void act('cancel')}>Cancel the rebuild</Button>
              : <Button variant="primary" disabled={busy || vaults.length === 0} onClick={() => void act('start')}>{vaults.some((v) => v.status?.state === 'done') ? 'Rebuild again' : 'Start the rebuild'}</Button>}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function VaultRow({ v }: { v: RebuildVault }) {
  const s = v.status;
  if (v.error || !s) return <li className="text-small text-fail">{v.vaultName}: {v.error ?? 'no answer'}</li>;
  const p = progressOf(s);
  const label = { idle: 'Not run', running: 'Rebuilding…', done: 'Done', failed: 'Failed', cancelled: 'Cancelled' }[s.state] ?? s.state;
  return (
    <li className="rounded-chip bg-n3 px-3 py-2.5 flex flex-col gap-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-small font-medium">{v.vaultName}</span>
        {s.state !== 'idle' && <span className="text-micro font-mono text-n7">{s.class ? s.class : 'all data models'}</span>}
        <span className={cx('text-micro', s.state === 'failed' ? 'text-fail' : s.state === RUNNING ? 'text-accent' : 'text-n8')}>{label}</span>
        <span className="flex-1" />
        {s.startedAt && <span className="text-micro text-n7">started {dateTime(s.startedAt)}{s.finishedAt ? ` · took ${duration(s.finishedAt - s.startedAt)}` : ''}</span>}
      </div>
      {s.state === RUNNING && (
        <>
          <div className="h-1.5 rounded-full bg-n4 overflow-hidden" role="progressbar" aria-valuenow={Math.round(p * 100)} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full bg-accent transition-[width] duration-300" style={{ width: `${Math.max(2, Math.round(p * 100))}%` }} />
          </div>
          <span className="text-micro text-n8">{count(s.docsScanned ?? 0)} documents read · day {s.partitionsDone ?? 0} of {s.partitionsTotal ?? 0}</span>
        </>
      )}
      {s.state === 'done' && (
        <span className="text-small text-n9">
          {count(s.fieldsBefore ?? 0)} → {count(s.fieldsAfter ?? 0)} fields{(s.removedFields ?? 0) > 0 ? ` · ${count(s.removedFields!)} removed` : ''} · {count(s.docsScanned ?? 0)} documents read
        </span>
      )}
      {s.state === 'failed' && <span className="text-small text-fail">{s.error ?? 'The rebuild failed; the model is unchanged.'}</span>}
      {s.state === 'cancelled' && <span className="text-small text-n8">Stopped; the model is unchanged.</span>}
    </li>
  );
}
